<template>
  <div class="content-grid content-grid--subpage">
    <Breadcrumbs :title="t(`routes.admin.children.organizations.editMember.name`)"/>
    <BackLinkTitle :title="t(`routes.admin.children.organizations.editMember.name`)"/>
    <Card>
      <MemberForm v-if="organization" :is-syndicate-organization="isSyndicate"
                  :member="existingMember"
                  @formSubmit="handleSubmit"/>
      <Loader v-else :is-inline="true" />
    </Card>
    <AdminResetMemberPassword v-if="memberStore.hasRole(Role.Admin)"
                              :organization-id="props.organizationId"
                              :member-id="props.memberId"/>
  </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from "vue"
import {useI18n} from "vue3-i18n"
import {useMemberService, useOrganizationService} from "@/inversify.config"
import {Member} from "@/types/entities/member"
import Breadcrumbs from "@/components/layout/Breadcrumbs.vue"
import BackLinkTitle from "@/components/layout/BackLinkTitle.vue"
import Card from "@/components/layout/Card.vue"
import MemberForm from "@/components/forms/MemberForm.vue"
import {notifyError, notifySuccess} from "@/notify";
import {useRouter} from "vue-router";
import {useMemberStore} from "@/stores/memberStore";
import {OrganizationType, Role} from "@/types";
import {IFullOrganization} from "@/types/entities/organization";
import Loader from "@/components/layout/Loader.vue";
import AdminResetMemberPassword from "@/views/admin/AdminResetMemberPassword.vue";

// eslint-disable-next-line no-undef
const props = defineProps<{
  organizationId?: string,
  memberId: string
}>()

const {t, getLocale} = useI18n()
const router = useRouter()
const memberStore = useMemberStore()
const memberService = useMemberService()
const organizationService = useOrganizationService()

const organizationId = props.organizationId ? props.organizationId : memberStore.getMember.organizationId
const existingMember = ref<Member>(await memberService.getMember(organizationId, props.memberId))
const hasIndividualAndCoOwnerRoles = ref<boolean>(existingMember.value.roles != undefined &&
  existingMember.value.roles.includes(Role.RegularIndividual) &&
  existingMember.value.roles.includes(Role.RegularSyndicateCoOwner))

let organization = ref<IFullOrganization>()
let isSyndicate = ref<boolean>(false)

onMounted(async () => {
  organization.value = memberStore.hasRole(Role.Admin)
      ? await organizationService.getOrganizationWithId(organizationId)
      : await organizationService.getMyOrganization(getLocale())
  isSyndicate.value = organization.value?.type === OrganizationType.RegularSyndicate
})

async function handleSubmit(member: Member) {
  if (isSyndicate.value && hasIndividualAndCoOwnerRoles.value && member.jobTitle == t('member.jobTitle.administrator.value')) {
    notifyError(t('validation.member.edit.individualMemberCannotHaveSyndicateAdminRole'))
    return;
  }

  let succeededOrNotResponse = await memberService.updateMember(member, organizationId)
  if (succeededOrNotResponse && succeededOrNotResponse.succeeded) {
    notifySuccess(t('validation.member.edit.success'))
    setTimeout(() => {
      router.back();
    }, 1500);
  } else {
    let errorMessages = succeededOrNotResponse.getErrorMessages('validation.member.edit');
    if (errorMessages.length == 0)
      notifyError(t('validation.member.edit.errorOccured'))
    else
      notifyError(errorMessages[0])
  }
}
</script>
